import axios from "axios";

// Empty string = same origin (works when React is served from FastAPI via ngrok)
// "http://localhost:8000" = local dev with separate React dev server
const BASE_URL = process.env.REACT_APP_API_URL ?? "";

// ── Auth token injection ───────────────────────────────────────────────────
// MsalAuthSetup calls setTokenProvider() once the user is logged in.
// Every request made through `ax` will then carry Authorization: Bearer <token>.
let _tokenProvider: (() => Promise<string | null>) | null = null;

export function setTokenProvider(fn: () => Promise<string | null>) {
  _tokenProvider = fn;
}

const ax = axios.create({ baseURL: BASE_URL });

ax.interceptors.request.use(async (config) => {
  if (_tokenProvider) {
    const token = await _tokenProvider();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Types ──────────────────────────────────────────────────────────────────

export interface DocumentChunk {
  content: string;
  source_file: string;
  page_number: number;
  score: number;
}

export interface ChatResponse {
  answer: string;
  sources: DocumentChunk[];
  conversation_id: string;
  knowledge_source: "documents" | "ai_knowledge" | "web_search";
}

export interface Document {
  name: string;
  size: number;
  last_modified: string | null;
}

export interface IndexStats {
  total_documents: number;
  total_chunks: number;
  index_name: string;
}

export interface UserInfo {
  id: number;
  email?: string | null;
  name?: string | null;
}

export interface ConversationSummary {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  sources?: DocumentChunk[];
  knowledge_source?: string;
  created_at: string;
}

export interface ConversationDetail {
  id: string;
  title: string;
  messages: ConversationMessage[];
  created_at: string;
}

export interface Incident {
  id: number;
  query: string;
  agent_used: string | null;
  error_type: string;
  error_message: string | null;
  created_at: string;
}

// ── API ────────────────────────────────────────────────────────────────────

export const api = {
  // ── Auth / user ─────────────────────────────────────────────────────────
  getMe: async (): Promise<UserInfo> => {
    const res = await ax.get("/api/me");
    return res.data;
  },

  // ── Chat ────────────────────────────────────────────────────────────────
  chat: async (query: string, conversationId?: string, signal?: AbortSignal): Promise<ChatResponse> => {
    const res = await ax.post("/api/chat", {
      query,
      conversation_id: conversationId,
    }, { signal });
    return res.data;
  },

  // ── Documents ───────────────────────────────────────────────────────────
  uploadDocument: async (file: File): Promise<{ filename: string; chunks_indexed: number; message: string }> => {
    const form = new FormData();
    form.append("file", file);
    const res = await ax.post("/api/documents/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  getDocuments: async (): Promise<Document[]> => {
    const res = await ax.get("/api/documents");
    return res.data;
  },

  getStats: async (): Promise<IndexStats> => {
    const res = await ax.get("/api/documents/stats");
    return res.data;
  },

  // ── Conversations ───────────────────────────────────────────────────────
  getConversations: async (): Promise<ConversationSummary[]> => {
    const res = await ax.get("/api/conversations");
    return res.data;
  },

  getConversation: async (id: string): Promise<ConversationDetail> => {
    const res = await ax.get(`/api/conversations/${id}`);
    return res.data;
  },

  deleteConversation: async (id: string): Promise<void> => {
    await ax.delete(`/api/conversations/${id}`);
  },

  // ── SharePoint sync ─────────────────────────────────────────────────────
  syncSharepoint: async (): Promise<{ synced: number; skipped: number; errors: number; total_chunks: number }> => {
    const res = await ax.post("/api/documents/sync-sharepoint");
    return res.data;
  },

  // ── Incidents ────────────────────────────────────────────────────────────
  getIncidents: async (limit = 100): Promise<Incident[]> => {
    const res = await ax.get(`/api/incidents?limit=${limit}`);
    return res.data;
  },

  deleteIncident: async (id: number): Promise<void> => {
    await ax.delete(`/api/incidents/${id}`);
  },

  clearIncidents: async (): Promise<void> => {
    await ax.delete("/api/incidents");
  },
};
